import { useState } from 'react';
import {
  importRespondentData,
  inspectRespondentData,
  type RespondentDataSheet,
} from '../../utils/surveyApi';
import type { RespondentDataBinding } from './aiDynamicSection';

/** Attach a file of per-respondent rows to a dynamic section (spec 50·05). The file is parsed and
 *  stored server-side; the section config keeps only the returned binding. */
export interface AiDynamicSectionRespondentDataProps {
  surveyId: number | null;
  binding: RespondentDataBinding | null;
  onChange: (binding: RespondentDataBinding | null) => void;
}

const LABEL_CLASS = 'block text-sm font-medium text-gray-700 mb-1';
const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const BUTTON_CLASS = 'px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50';

const HEADING_TEXT = 'Respondent data';
const HELPER_TEXT =
  "Upload a CSV or Excel file with rows about each respondent, such as a panel's shopping history. Each respondent's own rows are given to the AI moderator, matched on a parameter of their survey link.";
const SAVE_FIRST_TEXT = 'Save the survey first to attach respondent data.';
const FILE_LABEL = 'Respondent data file';
const SHEET_LABEL = 'Sheet';
const KEY_COLUMN_LABEL = 'Respondent ID column';
const URL_PARAM_LABEL = 'URL parameter';
const URL_PARAM_HELPER = 'The survey-link parameter that carries the same id, e.g. ?player=…';
const IMPORT_TEXT = 'Import';
const IMPORTING_TEXT = 'Importing…';
const READING_TEXT = 'Reading file…';
const REMOVE_TEXT = 'Remove';
const REMOVE_LABEL = 'Remove respondent data';
const DEFAULT_URL_PARAM = 'player';
const ACCEPTED_FILES = '.csv,.xlsx';
const FILE_INPUT_ID = 'ads-respondent-data-file';
const SHEET_ID = 'ads-respondent-data-sheet';
const KEY_ID = 'ads-respondent-data-key';
const PARAM_ID = 'ads-respondent-data-param';

const errorText = (err: unknown): string => (err instanceof Error ? err.message : String(err));

const keyColumnFor = (sheet: RespondentDataSheet | undefined, urlParam: string): string => {
  const columns = sheet?.columns ?? [];
  const wanted = urlParam.trim().toLowerCase();
  return columns.find((c) => wanted && c.toLowerCase().includes(wanted)) ?? columns[0] ?? '';
};

/** The largest sheet that has a column named like the URL param, else the first sheet. */
const defaultSheetIndex = (sheets: RespondentDataSheet[], urlParam: string): number => {
  const wanted = urlParam.toLowerCase();
  let best = -1;
  sheets.forEach((sheet, i) => {
    const matches = sheet.columns.some((c) => c.toLowerCase().includes(wanted));
    if (matches && (best < 0 || sheet.row_count > sheets[best].row_count)) best = i;
  });
  return Math.max(best, 0);
};

export function AiDynamicSectionRespondentData({
  surveyId,
  binding,
  onChange,
}: AiDynamicSectionRespondentDataProps) {
  const [file, setFile] = useState<File | null>(null);
  const [sheets, setSheets] = useState<RespondentDataSheet[]>([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [keyColumn, setKeyColumn] = useState('');
  const [urlParam, setUrlParam] = useState(DEFAULT_URL_PARAM);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setSheets([]);
    setKeyColumn('');
  };

  const chooseFile = async (picked: File | undefined) => {
    if (!picked || surveyId === null) return;
    setError(null);
    setBusy(READING_TEXT);
    try {
      const found = await inspectRespondentData(surveyId, picked);
      const index = defaultSheetIndex(found, urlParam);
      setFile(picked);
      setSheets(found);
      setSheetIndex(index);
      setKeyColumn(keyColumnFor(found[index], urlParam));
    } catch (err) {
      reset();
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const chooseSheet = (index: number) => {
    setSheetIndex(index);
    setKeyColumn(keyColumnFor(sheets[index], urlParam));
  };

  const runImport = async () => {
    if (!file || surveyId === null) return;
    setError(null);
    setBusy(IMPORTING_TEXT);
    try {
      const bound = await importRespondentData(surveyId, file, {
        sheet: sheets[sheetIndex]?.name ?? null,
        keyColumn,
        urlParam: urlParam.trim(),
      });
      reset();
      onChange(bound);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const sheet = sheets[sheetIndex];

  return (
    <div>
      <span className={LABEL_CLASS}>{HEADING_TEXT}</span>
      <p className="text-xs text-gray-500 mb-2">{HELPER_TEXT}</p>

      {binding ? (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 p-3">
          <div className="text-sm text-gray-700 min-w-0">
            <p>
              {binding.respondent_count} respondents · {binding.row_count} rows from{' '}
              {binding.sheet ? `${binding.file_name} › ${binding.sheet}` : binding.file_name}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Matched on link parameter <code>{binding.url_param}</code> = column{' '}
              <code>{binding.key_column}</code>
            </p>
          </div>
          <button
            type="button"
            aria-label={REMOVE_LABEL}
            onClick={() => onChange(null)}
            className={BUTTON_CLASS}
          >
            {REMOVE_TEXT}
          </button>
        </div>
      ) : surveyId === null ? (
        <p className="text-xs text-gray-500">{SAVE_FIRST_TEXT}</p>
      ) : (
        <div className="space-y-3">
          <div>
            <label htmlFor={FILE_INPUT_ID} className="sr-only">
              {FILE_LABEL}
            </label>
            <input
              id={FILE_INPUT_ID}
              type="file"
              accept={ACCEPTED_FILES}
              disabled={busy !== null}
              onChange={(e) => chooseFile(e.target.files?.[0])}
              className="text-sm"
            />
          </div>

          {file && sheet && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {sheets.length > 1 && (
                <div>
                  <label htmlFor={SHEET_ID} className={LABEL_CLASS}>
                    {SHEET_LABEL}
                  </label>
                  <select
                    id={SHEET_ID}
                    value={sheetIndex}
                    onChange={(e) => chooseSheet(Number(e.target.value))}
                    className={INPUT_CLASS}
                  >
                    {sheets.map((s, i) => (
                      <option key={i} value={i}>
                        {s.name ?? file.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label htmlFor={KEY_ID} className={LABEL_CLASS}>
                  {KEY_COLUMN_LABEL}
                </label>
                <select
                  id={KEY_ID}
                  value={keyColumn}
                  onChange={(e) => setKeyColumn(e.target.value)}
                  className={INPUT_CLASS}
                >
                  {sheet.columns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={PARAM_ID} className={LABEL_CLASS}>
                  {URL_PARAM_LABEL}
                </label>
                <input
                  id={PARAM_ID}
                  type="text"
                  value={urlParam}
                  onChange={(e) => setUrlParam(e.target.value)}
                  className={INPUT_CLASS}
                />
                <p className="text-xs text-gray-500 mt-1">{URL_PARAM_HELPER}</p>
              </div>
            </div>
          )}

          {file && sheet && (
            <button
              type="button"
              onClick={runImport}
              disabled={busy !== null || !keyColumn || !urlParam.trim()}
              className={BUTTON_CLASS}
            >
              {busy === IMPORTING_TEXT ? IMPORTING_TEXT : IMPORT_TEXT}
            </button>
          )}
          {busy === READING_TEXT && <p className="text-xs text-gray-500">{READING_TEXT}</p>}
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
